import { Injectable } from '@angular/core';
import { FunctionsApiService } from 'src/app/shared/services/functions-api.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { RecaptchaClientService } from './recaptcha-client.service';
import { VerifyRecaptchaData, VerifyRecaptchaResult } from 'src/lib/fliko/models/http/verifyRecaptcha.data';

interface LoginAttemptsCache {
  failedAttempts: number;
  updatedAt: number;
}

@Injectable({ providedIn: 'root' })
export class LoginCaptchaService {
  private readonly failedAttemptsThreshold = 3;
  private readonly failedAttemptsTTLms = 15 * 60 * 1000;
  private readonly recaptchaVerificationAttempts = 2;
  private readonly failedAttemptErrorCodes = [
    'auth/wrong-password',
    'auth/user-not-found',
    'auth/invalid-login-credentials',
    'auth/invalid-credential',
    'auth/too-many-requests'
  ];

  constructor(
    private functionsApi: FunctionsApiService,
    private recaptchaClient: RecaptchaClientService,
    private localStorageSrv: LocalStorageService
  ) {}

  /**
   * Checks if auth error should increase failed login attempts counter
   * @param {string | null | undefined} errorCode
   * @return {boolean}
   */
  public shouldCountAsFailedAttempt(errorCode: string | null | undefined): boolean {
    return this.failedAttemptErrorCodes.includes(errorCode ?? '');
  }

  /**
   * Checks if captcha is required for provided email
   * @param {string | null | undefined} email
   * @return {boolean}
   */
  public requiresCaptcha(email: string | null | undefined): boolean {
    return this.getFailedAttempts(email) >= this.failedAttemptsThreshold;
  }

  /**
   * Registers failed login attempt for provided email
   * @param {string | null | undefined} email
   * @return {boolean}
   */
  public registerFailedAttempt(email: string | null | undefined): boolean {
    const failedAttempts = this.getFailedAttempts(email) + 1;
    this.setFailedAttempts(email, failedAttempts);
    return failedAttempts >= this.failedAttemptsThreshold;
  }

  /**
   * Clears failed login attempts for provided email
   * @param {string | null | undefined} email
   */
  public clearFailedAttempts(email: string | null | undefined): void {
    this.setFailedAttempts(email, 0);
  }

  /**
   * Validates reCAPTCHA token for login action
   * @param {string | null | undefined} siteKey
   * @param {string}                    action
   * @return {Promise<boolean>}
   */
  public async validateRecaptchaForLogin(siteKey: string | null | undefined, action: string): Promise<boolean> {
    if (!siteKey) return false;

    for (let attempt = 1; attempt <= this.recaptchaVerificationAttempts; attempt++) {
      const token = await this.recaptchaClient.executeAction(siteKey, action);
      if (!token) continue;

      if (await this.verifyRecaptchaToken(token, action)) return true;
    }

    return false;
  }

  /**
   * Verifies reCAPTCHA token on backend and checks action consistency
   * @param {string} token
   * @param {string} action
   * @return {Promise<boolean>}
   */
  private async verifyRecaptchaToken(token: string, action: string): Promise<boolean> {
    const data: VerifyRecaptchaData = { token, action };
    const response = await this.functionsApi.callFunction<VerifyRecaptchaResult>('verifyRecaptcha', data, (reason) =>
      console.error('verifyRecaptcha failed', reason)
    );
    return response?.success === true && response?.action === action;
  }

  /**
   * Reads failed attempts from local storage and applies TTL validation
   * @param {string | null | undefined} email
   * @return {number}
   */
  private getFailedAttempts(email: string | null | undefined): number {
    const normalizedEmail = this.normalizeEmail(email);
    if (!normalizedEmail) return 0;

    const raw = this.localStorageSrv.loginAttempts.get(normalizedEmail);
    if (!raw) return 0;

    try {
      const cache = JSON.parse(raw) as LoginAttemptsCache;
      if (typeof cache.failedAttempts !== 'number' || typeof cache.updatedAt !== 'number') throw new Error();

      const hasExpired = Date.now() - cache.updatedAt > this.failedAttemptsTTLms;
      if (hasExpired) {
        this.localStorageSrv.loginAttempts.remove(normalizedEmail);
        return 0;
      }

      return Math.max(0, cache.failedAttempts);
    } catch {
      this.localStorageSrv.loginAttempts.remove(normalizedEmail);
      return 0;
    }
  }

  /**
   * Persists failed attempts value for provided email
   * @param {string | null | undefined} email
   * @param {number}                    failedAttempts
   */
  private setFailedAttempts(email: string | null | undefined, failedAttempts: number): void {
    const normalizedEmail = this.normalizeEmail(email);
    if (!normalizedEmail) return;

    if (failedAttempts <= 0) {
      this.localStorageSrv.loginAttempts.remove(normalizedEmail);
      return;
    }

    const cache: LoginAttemptsCache = { failedAttempts, updatedAt: Date.now() };
    this.localStorageSrv.loginAttempts.set(normalizedEmail, JSON.stringify(cache));
  }

  /**
   * Normalizes email value used as local storage key
   * @param {string | null | undefined} email
   * @return {string}
   */
  private normalizeEmail(email: string | null | undefined): string {
    return String(email ?? '')
      .trim()
      .toLowerCase();
  }
}

